/**
 * Server settings > Roles, at Discord's level (docs/design/roles-permissions.md):
 *
 * - the list: search, member counts, icons, drag (or Alt+Arrow) to reorder
 *   below your highest role, and the note on how voice channels sort;
 * - a role: Display (name, colour, icon image or emoji, show separately,
 *   mentionable), Permissions (Discord's groups with descriptions, search,
 *   Clear permissions, the Administrator warning) and Members (who has it,
 *   add, remove); Duplicate, View server as this role, Delete;
 * - the hierarchy exactly as the server applies it (`canManageRole`, the
 *   protocol's ranks): roles at or above your highest role are read-only,
 *   for Administrators too, and you give or take only permissions you hold.
 */
import {
  isSingleEmoji,
  NxPermissions,
  parsePermissions,
  Permissions,
  serializePermissions,
  type CreateRoleRequest,
  type GuildMember,
  type Role,
  type RolePokeRule,
  type Snowflake,
} from '@neosocial/protocol';
import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { plainText, useT } from '../i18n';
import { api, isApiRequestError } from '../lib/api';
import { assetUrl } from '../lib/config';
import { RoleIconProblem, roleIconAsDataUri, shrinkRoleIcon } from '../lib/roleIcon';
import { flagOn, useRoleIconMode } from '../store/config';
import { useData } from '../store/data';
import { canManageRole, displayName, roleRankIn, sortedRoles } from '../store/selectors';
import type { AdminState } from '../store/ui';
import { Avatar, Btn, Icon, Select, Switch } from '../ui/kit';
import { confirmDialog, Dialog, toast } from '../ui/overlay';
import { useTopOnChange } from '../ui/scroll';
import { openEmoji } from '../hub/emoji';
import { RoleIconView } from '../hub/RoleIcon';
import { runAction, withStepUpRetry } from '../mod/actions';
import { DESTRUCTIVE, useAdminPerms } from './adminPerms';
import { rebaseRole, roleChanges, roleFields, roleSig, sameRole, type RoleFields } from './drafts';
import { EDITABLE_BITS, EDITABLE_NX_BITS, ROLE_PERM_GROUPS, type PermDef } from './permDefs';
import { rolePositions, useDragOrder } from './reorder';
import { holdForUnsaved, useUnsaved } from './unsaved';
import { startViewAs } from './ViewAs';
import { SplitBack, UnsavedBar, useSetStep, useSplitCompact } from '../settings/Layer';

const hexOf = (c: number) => '#' + c.toString(16).padStart(6, '0');

/** What the icon of the draft is. */
type IconDraft =
  | { kind: 'keep' }
  | { kind: 'image'; uri: string }
  | { kind: 'emoji'; value: string }
  | { kind: 'none' };

function membersWith(members: Record<Snowflake, GuildMember> | undefined, roleId: Snowflake) {
  return Object.values(members ?? {}).filter((m) => m.roles.includes(roleId));
}

/* ------------------------------------------------------------------ the list */

export function RolesTab({ sel, onSel }: { sel: AdminState; onSel(s: AdminState): void }) {
  const { t, fmt } = useT();
  const ap = useAdminPerms();
  const gid = ap.gid;
  const guild = useData((d) => (gid ? d.guilds[gid] : undefined));
  const members = useData((d) => (gid ? d.members[gid] : undefined));
  const roles = useMemo(() => sortedRoles(guild), [guild]);
  const [q, setQ] = useState('');
  // another role opens at the top of its editor, not where the last one was scrolled
  const main = useRef<HTMLDivElement>(null);
  useTopOnChange(main, sel.id ?? '');
  // Unsaved edits are not dropped by picking another role (Discord holds you too).
  const go = (id: Snowflake | 'new') => {
    if (id !== sel.id && holdForUnsaved()) return;
    onSel({ tab: 'roles', id });
  };
  // phones and portrait tablets: the list, or the editor pushed over it (Back: the list)
  const compact = useSplitCompact();
  useSetStep(compact && !!sel.id, () => holdForUnsaved() || onSel({ tab: 'roles' }));
  const query = plainText(q).trim().toLowerCase();
  const shown = query
    ? roles.filter((r) => plainText(r.name).toLowerCase().includes(query))
    : roles;
  const selRole = sel.id && sel.id !== 'new' ? (roles.find((r) => r.id === sel.id) ?? null) : null;
  const movable = useMemo(() => roles.filter((r) => r.id !== gid), [roles, gid]);
  const roleIds = useMemo(() => movable.map((r) => r.id), [movable]);
  const canMove = (id: Snowflake) => {
    const r = movable.find((x) => x.id === id);
    return (
      !query && ap.roles && !!r && !!guild && (ap.isOwner || roleRankIn(guild, r) < ap.topRank)
    );
  };
  const dragRole = useDragOrder(
    roleIds,
    (next) => {
      if (!gid) return;
      const body = rolePositions(next, movable);
      if (!body.length) return;
      void runAction(() => withStepUpRetry(() => api.um.rolePositions(gid, body)), {
        quiet: true,
      }).then((r) => {
        if (r.ok) toast(t('um.drag.saved'), { icon: 'check', tone: 'ok' });
        else if (isApiRequestError(r.error))
          toast(t('roles.cantMove'), { tone: 'danger', icon: 'alert' });
      });
    },
    canMove,
  );
  return (
    <div className="ns-adm-split" data-show={compact ? (sel.id ? 'editor' : 'list') : undefined}>
      <div className="ns-adm-list nx-scroll">
        {ap.roles ? (
          <Btn size="sm" icon="plus" kind="mode" onClick={() => go('new')}>
            {t('ns.admin.newRole')}
          </Btn>
        ) : null}
        <label className="ns-find sm">
          <Icon name="search" />
          <input
            type="search"
            dir="auto"
            value={q}
            placeholder={t('roles.search')}
            aria-label={t('roles.search')}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <p className="ns-help ns-voice-order">{t('roles.voiceOrder')}</p>
        <ul className="ns-role-list">
          {shown.map((r) => {
            const n =
              r.id === gid ? Object.keys(members ?? {}).length : membersWith(members, r.id).length;
            return (
              <li
                key={r.id}
                {...(r.id !== gid ? dragRole(roleIds.indexOf(r.id)) : {})}
                title={r.id !== gid && canMove(r.id) ? t('um.drag.hint') : undefined}
                data-role={r.id}
              >
                <button
                  type="button"
                  className="ns-adm-item"
                  aria-current={sel.id === r.id ? 'true' : undefined}
                  onClick={() => go(r.id)}
                >
                  <i
                    className="ns-role-dot"
                    style={
                      { background: r.color ? hexOf(r.color) : 'var(--text-3)' } as CSSProperties
                    }
                  />
                  {r.icon || r.unicode_emoji ? (
                    <RoleIconView role={r} size={16} decorative />
                  ) : null}
                  <span className="ns-role-name" dir="auto">
                    {r.id === gid ? '@everyone' : r.name}
                  </span>
                  <small className="ns-role-n nx-num" aria-label={t('c.members', { n })}>
                    {fmt.num(n)}
                  </small>
                </button>
              </li>
            );
          })}
        </ul>
        {!shown.length ? <p className="ns-muted sm">{t('roles.searchNone')}</p> : null}
      </div>
      <div className="ns-adm-main nx-scroll" ref={main}>
        {compact && sel.id ? <SplitBack label={t('ns.admin.manageRoles')} /> : null}
        {sel.id && (sel.id === 'new' || selRole) ? (
          <RoleEditor key={sel.id} role={selRole} onDone={(id) => onSel({ tab: 'roles', id })} />
        ) : (
          <p className="ns-muted ns-pad">{t('ns.admin.pickRole')}</p>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- the editor */

type EditorTab = 'display' | 'perms' | 'members';

/**
 * The editor's state: the role as the edit began (`base`), the draft, the
 * live role last seen, and whether someone else changed it meanwhile.
 */
interface RoleEdit {
  base: RoleFields;
  draft: RoleFields;
  seen: string;
  elsewhere: boolean;
}

function RoleEditor({ role, onDone }: { role: Role | null; onDone(id?: Snowflake): void }) {
  const { t } = useT();
  const ap = useAdminPerms();
  const gid = ap.gid;
  const isEveryone = !!role && role.id === gid;
  const members = useData((d) => (gid ? d.members[gid] : undefined));
  // The hierarchy as the server checks it: Administrator does not lift it.
  const manageable = useData((d) => !!gid && (!role || canManageRole(d, gid, role)));
  const canEdit = ap.roles && manageable;
  const above = !!role && !manageable;
  const [tab, setTab] = useState<EditorTab>(isEveryone ? 'perms' : 'display');
  const [ed, setEd] = useState<RoleEdit>(() => {
    const f = roleFields(role);
    return { base: f, draft: f, seen: role ? roleSig(f) : '', elsewhere: false };
  });
  // The live role changed (here after a save, or by someone else): the draft
  // follows it, keeping only what was changed here (drafts.ts).
  const live = role ? roleFields(role) : null;
  const liveSig = live ? roleSig(live) : '';
  if (live && liveSig !== ed.seen) {
    const edited = !sameRole(ed.draft, ed.base);
    setEd(
      sameRole(live, ed.base)
        ? { ...ed, seen: liveSig }
        : {
            base: live,
            draft: rebaseRole(ed.draft, ed.base, live),
            seen: liveSig,
            elsewhere: ed.elsewhere || (edited && !sameRole(live, ed.draft)),
          },
    );
  }
  const { name, color, hoist, mentionable, perms, nx, pokesFrom } = ed.draft;
  const edit = (p: Partial<RoleFields>) => setEd((e) => ({ ...e, draft: { ...e.draft, ...p } }));
  const setName = (v: string) => edit({ name: v });
  const setColor = (v: number) => edit({ color: v });
  const setHoist = (v: boolean) => edit({ hoist: v });
  const setMentionable = (v: boolean) => edit({ mentionable: v });
  const setPerms = (v: bigint) => edit({ perms: v });
  const setNx = (v: bigint) => edit({ nx: v });
  const setPokesFrom = (v: RolePokeRule) => edit({ pokesFrom: v });
  // like a bit, the rule changes only with nx Poke (the server's 403; owner and Administrator hold it)
  const canPokeRule =
    ap.isOwner || ap.admin || (ap.base.nx_permissions & NxPermissions.POKE) !== 0n;
  const [icon, setIcon] = useState<IconDraft>({ kind: 'keep' });
  const [busy, setBusy] = useState(false);
  const dirty = !role || !sameRole(ed.draft, ed.base) || icon.kind !== 'keep';
  // A new role counts as unsaved once something is typed or picked.
  const unsaved = role
    ? dirty
    : !!name.trim() ||
      perms !== 0n ||
      nx !== 0n ||
      pokesFrom !== 'everyone' ||
      icon.kind !== 'keep' ||
      color !== 0;
  useUnsaved('role', canEdit && unsaved);
  const reset = () => {
    setEd((e) => ({ ...e, draft: e.base, elsewhere: false }));
    setIcon({ kind: 'keep' });
  };
  const count = role
    ? isEveryone
      ? Object.keys(members ?? {}).length
      : membersWith(members, role.id).length
    : 0;

  /** The icon the preview shows. */
  const previewIcon: Pick<Role, 'name' | 'icon' | 'unicode_emoji'> =
    icon.kind === 'image'
      ? { name, icon: icon.uri, unicode_emoji: null }
      : icon.kind === 'emoji'
        ? { name, icon: null, unicode_emoji: icon.value }
        : icon.kind === 'none'
          ? { name, icon: null, unicode_emoji: null }
          : { name, icon: role?.icon ?? null, unicode_emoji: role?.unicode_emoji ?? null };

  const save = async () => {
    if (!gid) return;
    setBusy(true);
    // An existing role: only what changed here, so a change made elsewhere
    // meanwhile (another permission, the colour) is never undone.
    const fields = role
      ? roleChanges(ed.draft, ed.base, isEveryone)
      : {
          name: name.trim(),
          color,
          hoist,
          mentionable,
          permissions: serializePermissions(perms),
          nx_permissions: serializePermissions(nx),
          ...(pokesFrom !== 'everyone' ? { nx_pokes_from: pokesFrom } : {}),
        };
    const body: CreateRoleRequest = {
      ...fields,
      ...(icon.kind === 'image'
        ? { icon: icon.uri }
        : icon.kind === 'emoji'
          ? { unicode_emoji: icon.value }
          : icon.kind === 'none'
            ? { icon: null, unicode_emoji: null }
            : {}),
    };
    const r = await runAction(() =>
      withStepUpRetry(
        () => (role ? api.guilds.modifyRole(gid, role.id, body) : api.guilds.createRole(gid, body)),
        DESTRUCTIVE,
      ),
    );
    setBusy(false);
    if (r.ok) {
      if (role) {
        // Saved: this is the new starting point (`seen` stays the live role
        // last seen, so a gateway update still on its way is no surprise).
        const saved = roleFields(r.value);
        setEd((e) => ({ ...e, base: saved, draft: saved, elsewhere: false }));
        setIcon({ kind: 'keep' });
      }
      toast(t('ns.saved'), { icon: 'check', tone: 'ok' });
      onDone(r.value.id);
    }
  };

  // A copy grants every bit of the role again, so (as the server checks) only
  // someone who holds all of them may make one: the owner, an Administrator,
  // or a Manage Roles holder with each of its Discord and nx bits.
  const canDuplicate =
    !!role &&
    ap.roles &&
    (ap.isOwner ||
      ap.admin ||
      ((parsePermissions(role.permissions) & ~ap.base.permissions) === 0n &&
        (parsePermissions(role.nx_permissions) & ~ap.base.nx_permissions) === 0n));

  const duplicate = async () => {
    if (!gid || !role || isEveryone) return;
    if (dirty) {
      toast(t('roles.saveFirst'), { tone: 'danger', icon: 'alert' });
      return;
    }
    // With the console's Image uploads off, the server refuses any image
    // icon, so the copy goes without it (and says so) rather than failing
    // whole (fix round 2026-10-28)
    const uploads = flagOn('image_uploads');
    const iconUri =
      role.icon && uploads ? await roleIconAsDataUri(assetUrl(role.icon) ?? role.icon) : null;
    const body: CreateRoleRequest = {
      // stored text: without the direction isolates the translator puts around names
      name: plainText(t('roles.copyName', { name: role.name })).slice(0, 100),
      color: role.color,
      hoist: role.hoist,
      mentionable: role.mentionable,
      permissions: role.permissions,
      nx_permissions: role.nx_permissions,
      ...(role.nx_pokes_from && role.nx_pokes_from !== 'everyone' && canPokeRule
        ? { nx_pokes_from: role.nx_pokes_from }
        : {}),
      ...(iconUri
        ? { icon: iconUri }
        : role.unicode_emoji
          ? { unicode_emoji: role.unicode_emoji }
          : {}),
    };
    const r = await runAction(() =>
      withStepUpRetry(() => api.guilds.createRole(gid, body), DESTRUCTIVE),
    );
    if (r.ok) {
      if (role.icon && !uploads)
        toast(t('roles.duplicatedNoIcon'), { icon: 'copy', tone: 'warn', duration: 6000 });
      else toast(t('roles.duplicated'), { icon: 'copy', tone: 'ok' });
      onDone(r.value.id);
    }
  };

  const remove = async () => {
    if (!role || !gid || isEveryone) return;
    const ok = await confirmDialog({
      title: t('ns.admin.deleteRole', { name: role.name }),
      body: t('ns.admin.deleteRoleBody'),
      confirmLabel: t('ns.admin.delete'),
      danger: true,
      icon: 'trash',
    });
    if (!ok) return;
    const r = await runAction(() => api.guilds.deleteRole(gid, role.id), {
      challenge: { reason: 'destructive', level: 'second_factor' },
    });
    if (r.ok) onDone();
  };

  const tabs: Array<{ id: EditorTab; label: string }> = [
    ...(!isEveryone ? [{ id: 'display' as const, label: t('roles.tab.display') }] : []),
    { id: 'perms', label: t('roles.tab.perms') },
    ...(role && !isEveryone
      ? [{ id: 'members' as const, label: t('roles.tab.members', { n: count }) }]
      : []),
  ];

  return (
    <div className="ns-adm-ed ns-role-ed" data-role={role?.id ?? 'new'}>
      <div className="ns-role-ed-h">
        <h3 dir="auto">{role ? (isEveryone ? '@everyone' : role.name) : t('ns.admin.newRole')}</h3>
        <span className="ns-fill" />
        {role && (ap.isOwner || ap.admin) ? (
          <Btn
            size="xs"
            icon="eye"
            onClick={() => {
              // the preview closes Server settings: not over unsaved edits
              if (gid && !holdForUnsaved()) startViewAs(gid, isEveryone ? [] : [role.id]);
            }}
          >
            {t('roles.viewAs')}
          </Btn>
        ) : null}
        {role && !isEveryone && canDuplicate ? (
          <Btn size="xs" icon="copy" onClick={() => void duplicate()}>
            {t('roles.duplicate')}
          </Btn>
        ) : null}
        {role && !isEveryone ? (
          <Btn
            kind="danger"
            size="xs"
            icon="trash"
            disabled={!canEdit}
            onClick={() => void remove()}
          >
            {t('ns.admin.delete')}
          </Btn>
        ) : null}
      </div>
      {above ? (
        <p className="ns-note warn" data-locked="true">
          <Icon name="lock" /> {t('ns.admin.roleAbove')}
        </p>
      ) : null}
      {ed.elsewhere && dirty ? (
        <p className="ns-note warn" data-elsewhere="true">
          <Icon name="alert" /> {t('ns.admin.changedElsewhere')}
        </p>
      ) : null}
      {isEveryone ? <p className="ns-muted">{t('ns.admin.everyoneNote')}</p> : null}
      {tabs.length > 1 ? (
        <div className="ns-subtabs" role="tablist">
          {tabs.map((x) => (
            <button
              key={x.id}
              type="button"
              role="tab"
              aria-selected={tab === x.id}
              data-tab={x.id}
              onClick={() => setTab(x.id)}
            >
              {x.label}
            </button>
          ))}
        </div>
      ) : null}
      {tab === 'display' && !isEveryone ? (
        <DisplayTab
          name={name}
          setName={setName}
          color={color}
          setColor={setColor}
          hoist={hoist}
          setHoist={setHoist}
          mentionable={mentionable}
          setMentionable={setMentionable}
          icon={icon}
          setIcon={setIcon}
          previewIcon={previewIcon}
          disabled={!canEdit}
        />
      ) : null}
      {/* no "Talk power": it is not a Hub setting (hub-only decision, SA-B10) */}
      {tab === 'perms' ? (
        <PermsTab
          perms={perms}
          nx={nx}
          setPerms={setPerms}
          setNx={setNx}
          pokesFrom={pokesFrom}
          setPokesFrom={setPokesFrom}
          canPokeRule={canPokeRule}
          disabled={!canEdit}
        />
      ) : null}
      {tab === 'members' && role && gid ? (
        <MembersTab guildId={gid} role={role} canEdit={canEdit} />
      ) : null}
      {canEdit && (dirty || !role) ? (
        <UnsavedBar
          text={role ? undefined : null}
          onReset={role || unsaved ? reset : undefined}
          busy={busy}
          saveDisabled={!isEveryone && !name.trim()}
          saveLabel={role ? t('c.save') : t('ns.admin.create')}
          onSave={() => void save()}
        />
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- display */

function DisplayTab(p: {
  name: string;
  setName(v: string): void;
  color: number;
  setColor(v: number): void;
  hoist: boolean;
  setHoist(v: boolean): void;
  mentionable: boolean;
  setMentionable(v: boolean): void;
  icon: IconDraft;
  setIcon(v: IconDraft): void;
  previewIcon: Pick<Role, 'name' | 'icon' | 'unicode_emoji'>;
  disabled: boolean;
}) {
  const { t } = useT();
  const me = useData((d) => d.me);
  const iconMode = useRoleIconMode();
  const file = useRef<HTMLInputElement>(null);
  const [typed, setTyped] = useState('');
  const [reading, setReading] = useState(false);
  const hasIcon = !!(p.previewIcon.icon || p.previewIcon.unicode_emoji);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    setReading(true);
    try {
      p.setIcon({ kind: 'image', uri: await shrinkRoleIcon(f) });
      toast(t('roles.icon.ready'), { icon: 'check', tone: 'ok' });
    } catch (e) {
      const reason = e instanceof RoleIconProblem ? e.reason : 'decode';
      toast(t(`roles.icon.err.${reason}`), { tone: 'danger', icon: 'alert' });
    } finally {
      setReading(false);
      if (file.current) file.current.value = '';
    }
  };
  return (
    <div className="ns-role-display">
      <label className="ns-field">
        <span className="ns-label">{t('ns.admin.name')}</span>
        <input
          className="nx-input"
          dir="auto"
          value={p.name}
          maxLength={100}
          disabled={p.disabled}
          onChange={(e) => p.setName(e.target.value)}
          data-autofocus
        />
      </label>
      <label className="ns-field">
        <span className="ns-label">{t('ns.admin.color')}</span>
        <span className="ns-color">
          <input
            type="color"
            value={hexOf(p.color)}
            disabled={p.disabled}
            onChange={(e) => p.setColor(parseInt(e.target.value.slice(1), 16))}
          />
          <code dir="ltr">{p.color ? hexOf(p.color) : t('ns.admin.noColor')}</code>
          {p.color ? (
            <button
              type="button"
              className="nx-chipbtn"
              disabled={p.disabled}
              onClick={() => p.setColor(0)}
            >
              {t('ns.admin.noColor')}
            </button>
          ) : null}
        </span>
      </label>
      <div className="ns-field ns-role-icon">
        <span className="ns-label">{t('roles.icon')}</span>
        <div className="ns-role-icon-row">
          <span className="ns-role-icon-big" aria-hidden={!hasIcon}>
            {hasIcon ? (
              <RoleIconView role={p.previewIcon} size={40} decorative />
            ) : (
              <Icon name="image" />
            )}
          </span>
          <div className="ns-role-icon-btns">
            <input
              ref={file}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => void pick(e.target.files?.[0])}
              data-role-icon-file
            />
            <Btn
              size="xs"
              icon="upload"
              disabled={p.disabled || reading}
              busy={reading}
              onClick={() =>
                // the console's Image uploads covers role icons too (an emoji stays open)
                flagOn('image_uploads')
                  ? file.current?.click()
                  : toast(t('cfg.off.image_uploads'), { icon: 'image', tone: 'warn' })
              }
            >
              {t('roles.icon.upload')}
            </Btn>
            <Btn
              size="xs"
              icon="smile"
              disabled={p.disabled}
              onClick={(e) =>
                openEmoji(
                  e.currentTarget,
                  (emoji) => p.setIcon({ kind: 'emoji', value: emoji }),
                  'bottom-start',
                )
              }
            >
              {t('roles.icon.emoji')}
            </Btn>
            {hasIcon ? (
              <Btn
                size="xs"
                kind="ghost"
                icon="trash"
                disabled={p.disabled}
                onClick={() => p.setIcon({ kind: 'none' })}
              >
                {t('roles.icon.remove')}
              </Btn>
            ) : null}
          </div>
        </div>
        <div className="ns-role-emoji-in">
          <input
            className="nx-input"
            value={typed}
            maxLength={32}
            disabled={p.disabled}
            placeholder={t('roles.emoji.custom')}
            aria-label={t('roles.emoji.custom')}
            onChange={(e) => setTyped(e.target.value)}
          />
          <Btn
            size="xs"
            disabled={p.disabled || !typed.trim()}
            onClick={() => {
              const v = typed.trim();
              if (!isSingleEmoji(v)) {
                toast(t('roles.emoji.bad'), { tone: 'danger', icon: 'alert' });
                return;
              }
              p.setIcon({ kind: 'emoji', value: v });
              setTyped('');
            }}
          >
            {t('roles.emoji.use')}
          </Btn>
        </div>
        <span className="ns-help">
          {iconMode === 'all' ? t('roles.icon.help.all') : t('roles.icon.help')}
        </span>
        <div className="ns-role-preview" aria-label={t('roles.icon.preview')}>
          <span className="ns-label">{t('roles.icon.preview')}</span>
          <div className="vc-u ns-role-preview-row">
            {hasIcon ? <RoleIconView role={p.previewIcon} size={16} cls="vc-ri" /> : null}
            <Avatar user={me} size={24} />
            <span className="vc-n" dir="auto">
              {me ? displayName(me) : ''}
            </span>
          </div>
          <div className="ns-role-preview-row msg">
            <span className="mem-name" dir="auto">
              {me ? displayName(me) : ''}
            </span>
            {hasIcon ? <RoleIconView role={p.previewIcon} size={16} decorative /> : null}
          </div>
        </div>
      </div>
      <div className="ns-field">
        <div className="ns-row">
          <span>{t('ns.admin.hoist')}</span>
          <Switch
            checked={p.hoist}
            disabled={p.disabled}
            label={t('ns.admin.hoist')}
            onChange={p.setHoist}
          />
        </div>
        <div className="ns-row">
          <span>{t('ns.admin.mentionable')}</span>
          <Switch
            checked={p.mentionable}
            disabled={p.disabled}
            label={t('ns.admin.mentionable')}
            onChange={p.setMentionable}
          />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ permissions */

function PermsTab(p: {
  perms: bigint;
  nx: bigint;
  setPerms(v: bigint): void;
  setNx(v: bigint): void;
  pokesFrom: RolePokeRule;
  setPokesFrom(v: RolePokeRule): void;
  /** You hold nx Poke (the owner and Administrators do): the server lets you change the rule. */
  canPokeRule: boolean;
  disabled: boolean;
}) {
  const { t } = useT();
  const ap = useAdminPerms();
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  /** You give or take only what you hold (owner and Administrator hold everything). */
  const hold = (d: PermDef) =>
    ap.isOwner ||
    ap.admin ||
    (d.nx ? (ap.base.nx_permissions & d.bit) !== 0n : (ap.base.permissions & d.bit) !== 0n);
  const match = (d: PermDef) =>
    !query || t(d.label).toLowerCase().includes(query) || t(d.desc).toLowerCase().includes(query);
  const groups = ROLE_PERM_GROUPS.map((g) => ({ ...g, perms: g.perms.filter(match) })).filter(
    (g) => g.perms.length,
  );
  const toggle = (d: PermDef, on: boolean) => {
    if (d.nx) p.setNx(on ? p.nx | d.bit : p.nx & ~d.bit);
    else p.setPerms(on ? p.perms | d.bit : p.perms & ~d.bit);
  };
  // Clear only what the editor shows and you may change; hidden bits stay.
  const clearable = (nx: boolean) =>
    ROLE_PERM_GROUPS.flatMap((g) => g.perms)
      .filter((d) => !!d.nx === nx && hold(d))
      .reduce((a, d) => a | d.bit, 0n);
  const anyOn =
    (p.perms & EDITABLE_BITS & clearable(false)) !== 0n ||
    (p.nx & EDITABLE_NX_BITS & clearable(true)) !== 0n;
  const admin = (p.perms & Permissions.ADMINISTRATOR) !== 0n;
  return (
    <div className="ns-role-perms">
      <div className="ns-row-btns">
        <label className="ns-find">
          <Icon name="search" />
          <input
            type="search"
            dir="auto"
            value={q}
            placeholder={t('roles.perm.search')}
            aria-label={t('roles.perm.search')}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <span className="ns-fill" />
        <Btn
          size="xs"
          kind="ghost"
          disabled={p.disabled || !anyOn}
          onClick={() => {
            p.setPerms(p.perms & ~clearable(false));
            p.setNx(p.nx & ~clearable(true));
          }}
        >
          {t('roles.perm.clear')}
        </Btn>
      </div>
      {admin ? (
        <p className="ns-note danger" data-warn="administrator">
          <Icon name="alert" /> {t('roles.perm.adminWarn')}
        </p>
      ) : null}
      {!groups.length ? <p className="ns-muted">{t('roles.perm.none')}</p> : null}
      {groups.map((g) => (
        <div key={g.title} className="ns-perm-group">
          <h4>{t(g.title)}</h4>
          {g.perms.map((d) => {
            const on = ((d.nx ? p.nx : p.perms) & d.bit) !== 0n;
            const mine = hold(d);
            return (
              <div
                key={d.key}
                className={'ns-row ns-perm-row' + (d.danger ? ' danger' : '')}
                data-perm={d.key}
              >
                <span className="ns-perm-txt">
                  <span>{t(d.label)}</span>
                  <small className="ns-muted">{t(d.desc)}</small>
                  {!mine ? <small className="ns-muted"> · {t('roles.perm.notYours')}</small> : null}
                </span>
                <Switch
                  checked={on}
                  disabled={p.disabled || !mine}
                  label={t(d.label)}
                  onChange={(v) => toggle(d, v)}
                />
              </div>
            );
          })}
        </div>
      ))}
      {!query || t('pk.role.label').toLowerCase().includes(query) ? (
        <PokeRulePicker
          value={p.pokesFrom}
          onChange={p.setPokesFrom}
          disabled={p.disabled}
          allowed={p.canPokeRule}
        />
      ) : null}
    </div>
  );
}

/**
 * "Who can poke members with this role" (`nx_pokes_from`, 2026-10-29): the
 * target's highest role decides. Changing it needs nx Poke, as a bit does
 * (the server answers 403 otherwise), so without it the picker is disabled
 * and says why.
 */
export function PokeRulePicker(p: {
  value: RolePokeRule;
  onChange(v: RolePokeRule): void;
  disabled: boolean;
  allowed: boolean;
}) {
  const { t } = useT();
  const off = p.disabled || !p.allowed;
  return (
    <div className="pk-rule" data-field="pokes-from">
      <label className="ns-field">
        <span className="ns-label">{t('pk.role.label')}</span>
        <Select
          className="nx-input"
          value={p.value}
          disabled={off}
          aria-label={t('pk.role.label')}
          onChange={(e) => p.onChange(e.target.value as RolePokeRule)}
        >
          <option value="everyone">{t('pk.role.everyone')}</option>
          <option value="higher">{t('pk.role.higher')}</option>
          <option value="nobody">{t('pk.role.nobody')}</option>
        </Select>
      </label>
      <span className="ns-help">{t('pk.role.help')}</span>
      {!p.allowed && !p.disabled ? (
        <p className="ns-note" data-note="needs-poke">
          <Icon name="lock" /> {t('pk.role.needPoke')}
        </p>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- members */

function MembersTab({
  guildId,
  role,
  canEdit,
}: {
  guildId: Snowflake;
  role: Role;
  canEdit: boolean;
}) {
  const { t } = useT();
  const members = useData((d) => d.members[guildId]);
  const [q, setQ] = useState('');
  const [picking, setPicking] = useState(false);
  const [busyId, setBusyId] = useState<Snowflake | null>(null);
  const query = q.trim().toLowerCase();
  const matches = (m: GuildMember) =>
    !query ||
    displayName(m.user, m).toLowerCase().includes(query) ||
    m.user.username.toLowerCase().includes(query);
  const holders = membersWith(members, role.id)
    .filter(matches)
    .sort((a, b) => displayName(a.user, a).localeCompare(displayName(b.user, b)));
  const take = async (m: GuildMember) => {
    setBusyId(m.user.id);
    const r = await runAction(() => api.um.removeMemberRole(guildId, m.user.id, role.id));
    setBusyId(null);
    if (r.ok) toast(t('roles.members.removed', { role: role.name }), { icon: 'check', tone: 'ok' });
  };
  return (
    <div className="ns-role-members">
      {!canEdit ? <p className="ns-note warn">{t('roles.members.cant')}</p> : null}
      <div className="ns-row-btns">
        <label className="ns-find">
          <Icon name="search" />
          <input
            type="search"
            dir="auto"
            value={q}
            placeholder={t('roles.members.search')}
            aria-label={t('roles.members.search')}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <span className="ns-fill" />
        <Btn
          size="sm"
          kind="mode"
          icon="user-plus"
          disabled={!canEdit}
          onClick={() => setPicking(true)}
        >
          {t('roles.members.add')}
        </Btn>
      </div>
      {!holders.length ? <p className="ns-muted">{t('roles.members.none')}</p> : null}
      <ul className="ns-role-holders">
        {holders.map((m) => (
          <li key={m.user.id} data-member={m.user.id}>
            <Avatar user={m.user} size={28} />
            <span className="ns-rh-n">
              <b dir="auto">{displayName(m.user, m)}</b>
              <small className="ns-muted" dir="ltr">
                @{m.user.username}
              </small>
            </span>
            {canEdit ? (
              <button
                type="button"
                className="nx-ibtn sm flat"
                aria-label={t('roles.members.remove', { name: displayName(m.user, m) })}
                data-tip={t('roles.members.remove', { name: displayName(m.user, m) })}
                disabled={busyId === m.user.id}
                onClick={() => void take(m)}
              >
                <Icon name="x" />
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {picking ? (
        <AddMembersDialog guildId={guildId} role={role} onClose={() => setPicking(false)} />
      ) : null}
    </div>
  );
}

function AddMembersDialog({
  guildId,
  role,
  onClose,
}: {
  guildId: Snowflake;
  role: Role;
  onClose(): void;
}) {
  const { t } = useT();
  const members = useData((d) => d.members[guildId]);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Snowflake[]>([]);
  const [busy, setBusy] = useState(false);
  const query = q.trim().toLowerCase();
  const list = Object.values(members ?? {})
    .filter((m) => !m.roles.includes(role.id) && !m.user.bot)
    .filter(
      (m) =>
        !query ||
        displayName(m.user, m).toLowerCase().includes(query) ||
        m.user.username.toLowerCase().includes(query),
    )
    .sort((a, b) => displayName(a.user, a).localeCompare(displayName(b.user, b)))
    .slice(0, 200);
  const add = async () => {
    setBusy(true);
    let done = 0;
    for (const id of picked) {
      const r = await runAction(() => api.um.addMemberRole(guildId, id, role.id));
      if (!r.ok) break;
      done += 1;
    }
    setBusy(false);
    if (done > 0)
      toast(t('roles.members.added', { role: role.name }), { icon: 'check', tone: 'ok' });
    if (done === picked.length) onClose();
  };
  return (
    <Dialog
      title={t('roles.members.pickTitle', { role: role.name })}
      icon="user-plus"
      onClose={onClose}
      stack
      cls="ns-role-pick"
      actions={
        <>
          <Btn onClick={onClose}>{t('c.cancel')}</Btn>
          <Btn
            kind="primary"
            disabled={!picked.length || busy}
            busy={busy}
            onClick={() => void add()}
          >
            {t('roles.members.addN', { n: picked.length })}
          </Btn>
        </>
      }
    >
      <label className="ns-find">
        <Icon name="search" />
        <input
          type="search"
          dir="auto"
          value={q}
          placeholder={t('roles.members.search')}
          aria-label={t('roles.members.search')}
          onChange={(e) => setQ(e.target.value)}
          data-autofocus
        />
      </label>
      {!list.length ? <p className="ns-muted">{t('roles.members.pickNone')}</p> : null}
      <ul className="ns-role-holders pick">
        {list.map((m) => {
          const on = picked.includes(m.user.id);
          return (
            <li key={m.user.id}>
              <label className="ns-check">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) =>
                    setPicked((p) =>
                      e.target.checked ? [...p, m.user.id] : p.filter((x) => x !== m.user.id),
                    )
                  }
                />
                <Avatar user={m.user} size={24} />
                <span dir="auto">{displayName(m.user, m)}</span>
                <small className="ns-muted" dir="ltr">
                  @{m.user.username}
                </small>
              </label>
            </li>
          );
        })}
      </ul>
    </Dialog>
  );
}
